import { Card, Grid, CrudMode, useOrderableList, Stack, IconButton, Tooltip, Typography, ValidationErrors } from "@compass/core";
import {
  Form,
  FormAnswer,
  FormQuestion,
  FormSection,
  FormSubQuestion,
  SurveyEditResource,
  SurveyViewResource,
} from "generated/openApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { SurveyQuestionContainer } from "./SurveyQuestionContainer";
import { useEffect, useRef, useState } from "react";
import { createEntity } from "utils/CreateEntity";

export const SurveyQuestions = (props: {
  survey: Form;
  copySurvey: number | undefined;
  setSurvey: (value: Form) => void;
  // updateQuestions: (value: FormQuestion[]) => void;
  viewResource: SurveyViewResource;
  editResource: SurveyEditResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
  refreshSurvey: Form | undefined;
  navigateEditMode: () => void;
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<FormQuestion | null>();
  const [questions, setQuestions] = useState<FormQuestion[]>();
  const syncingQuestionsFromSurvey = useRef(false);

  const getQuestionIdentity = (question: FormQuestion) =>
    question.id > 0 ? `id:${question.id}` : `temp:${question.tempId || question.displayOrder}`;

  const getUniqueQuestions = (questions: FormQuestion[]) => {
    const uniqueQuestions = new Map<string, FormQuestion>();

    questions.forEach((question) => {
      uniqueQuestions.set(getQuestionIdentity(question), question);
    });

    return Array.from(uniqueQuestions.values()).sort((a, b) => a.displayOrder - b.displayOrder);
  };

  useEffect(() => {
    syncingQuestionsFromSurvey.current = true;
    setQuestions(getUniqueQuestions(props.survey.formQuestions || []));
  }, [props.copySurvey, props.refreshSurvey]);

  useEffect(() => {
    if (!questions) return;

    if (syncingQuestionsFromSurvey.current) {
      syncingQuestionsFromSurvey.current = false;
      return;
    }

    props.setSurvey(
      produce(props.survey, (draft) => {
        if (draft) {
          draft.formQuestions = getUniqueQuestions(questions);
        }
      })
    );
  }, [questions]);

  const addQuestion = (displayOrder: number) => {
    setQuestions(
      produce((draft) => {
        let newQuestion: FormQuestion = createEntity<FormQuestion>({
          displayOrder,
          isMandatory: false,
        });

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.filter((q) => q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.push(newQuestion);

        draft?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedQuestion(newQuestion);
      })
    );
  };

  const copyQuestion = (displayOrder: number, copiedQuestion: FormQuestion) => {
    setQuestions(
      produce((draft) => {
        let newQuestion: FormQuestion = createEntity<FormQuestion>({
          name: copiedQuestion.name,
          label: copiedQuestion.label,
          instruction: copiedQuestion.instruction,
          formQuestionTypeId: copiedQuestion.formQuestionTypeId,
          formQuestionWriteInTypeId: copiedQuestion.formQuestionWriteInTypeId,
          hideSubQuestionLabel: copiedQuestion.hideSubQuestionLabel,
          hideLabel: copiedQuestion.hideLabel,
          hideQuestionName: copiedQuestion.hideQuestionName,
          hideSubQuestionName: copiedQuestion.hideSubQuestionName,
          isAlternatingRowColor: copiedQuestion.isAlternatingRowColor,
          displayOrder,
          isMandatory: copiedQuestion.isMandatory,
        });

        // add answers
        var copiedAnswers: FormAnswer[] = [];
        copiedQuestion.formAnswers?.forEach((a) => {
          let newAnswer: FormAnswer = createEntity<FormAnswer>({
            name: a.name,
            displayOrder: a.displayOrder,
            isOther: a.isOther,
            otherWidthPercent: a.otherWidthPercent,
          });
          copiedAnswers.push(newAnswer);
        });
        // add subQuestions
        var copiedSubQuestions: FormSubQuestion[] = [];
        copiedQuestion.formSubQuestions?.forEach((a) => {
          let newSubQuestion: FormSubQuestion = createEntity<FormSubQuestion>({
            name: a.name,
            label: a.label,
            displayOrder: a.displayOrder,
            isOther: a.isOther,
          });
          copiedSubQuestions.push(newSubQuestion);
        });
        newQuestion.formAnswers = copiedAnswers;
        newQuestion.formSubQuestions = copiedSubQuestions;

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.filter((q) => q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.push(newQuestion);
        draft?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedQuestion(newQuestion);
      })
    );
  };

  const updateQuestion = (question: FormQuestion) => {
    setQuestions(
      produce((draft) => {
        let draftQuestionIndex = draft?.findIndex((q) => getQuestionIdentity(q) == getQuestionIdentity(question));

        if (draftQuestionIndex !== undefined && draftQuestionIndex >= 0) {
          draft![draftQuestionIndex] = question;
        }
      })
    );
  };

  const getBranchableQuestions = (question: FormQuestion) : FormQuestion[] => {
    if (!questions) return [];
    return getUniqueQuestions(questions).filter((q) => getQuestionIdentity(q) !== getQuestionIdentity(question));
  }

  const getBranchableSections = () : FormSection[] => {
    return props.survey.formSections || [];
  }

  const renderQuestions = (question: FormQuestion, index: number) => {
    const formQuestionValidationErrors = props.validationErrors[`formQuestions`];
    let questionValidationErrors = {};
    if (formQuestionValidationErrors) {
      questionValidationErrors = formQuestionValidationErrors[index];
    }

    return (
      <SurveyQuestionContainer
        viewResource={props.viewResource}
        question={question}
        refreshSurvey={props.refreshSurvey}
        updateQuestion={updateQuestion}
        copyQuestion={copyQuestion}
        addQuestion={addQuestion}
        selected={!!selectedQuestion && getQuestionIdentity(selectedQuestion) == getQuestionIdentity(question)}
        setSelected={setSelectedQuestion}
        key={`${getQuestionIdentity(question)}:${index}`}
        navigateEditMode={props.navigateEditMode}
        validationErrors={questionValidationErrors}
        branchableQuestions={getBranchableQuestions(question)}
        branchableSections={getBranchableSections()}
      />
    );
  };

  return (
    <Grid container spacing="lg">
      {props.survey && getUniqueQuestions(questions || []).filter((q) => !q.deleted).map((q, i) => renderQuestions(q, i))}
      {props.survey && questions?.length == 0 && (
        <Grid item xs={10} display="flex" alignItems="center" justifyContent="center">
          <Card>
            <Typography>No questions yet, click to begin!</Typography>
            <IconButton onClick={() => addQuestion(1)}>
              <Tooltip title="Add New Question">
                <AddCircleOutlineIcon />
              </Tooltip>
            </IconButton>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};
